import React, { useMemo } from 'react'
import { Card, Button, Spinner } from 'react-bootstrap'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { selectSelectedModelId } from '../../../store/slices/modelSlice'
import { useGetModelTreeQuery, useGetProjectQuery } from '../../../services/projectsApi'
import { useGetModelQuery } from '../../../services/modelsApi'
import ModelTreeVisualization from '../../tree/ModelTreeVisualization'
import { useNavigate } from 'react-router-dom'
import { GitBranch } from 'lucide-react'

interface ModelVersionsViewProps {
  modelId?: string
  projectId?: string
}

const ModelVersionsView: React.FC<ModelVersionsViewProps> = ({
  modelId: propModelId,
  projectId: propProjectId,
}) => {
  const navigate = useNavigate()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)

  // Use prop values if provided, otherwise fall back to Redux store
  const modelId = propModelId || selectedModelId
  const projectId = propProjectId || selectedProjectId

  const {
    data: model,
    isLoading: modelLoading,
    error: modelError,
  } = useGetModelQuery(modelId || '', {
    skip: !modelId,
    refetchOnMountOrArgChange: false, // Use cached data if available
  })

  const {
    data: project,
    isLoading: projectLoading,
    error: projectError,
  } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
    refetchOnMountOrArgChange: false, // Use cached data if available
  })

  const {
    data: modelTree,
    isLoading: isTreeLoading,
    error: treeError,
  } = useGetModelTreeQuery(projectId || '', {
    skip: !projectId,
    refetchOnMountOrArgChange: false, // Use cached data if available
    refetchOnFocus: false, // Don't refetch on focus
    refetchOnReconnect: false, // Don't refetch on reconnect
  })

  // Get all parent models (models without a parent)
  const parentModels = useMemo(() => {
    if (!modelTree?.model_tree) return []
    return modelTree.model_tree.filter((node: any) => !node.parent_id)
  }, [modelTree])

  // Create a filtered tree that only includes ancestors and descendants of the selected model
  const filteredTreeData = useMemo(() => {
    if (!modelTree?.model_tree || !modelId) return null

    // Helper to find a node by model ID in the tree
    const findNodeById = (nodes: any[], modelId: string): any | null => {
      for (const node of nodes) {
        if (node.model.id === modelId) return node

        if (node.children && node.children.length > 0) {
          const found = findNodeById(node.children, modelId)
          if (found) return found
        }
      }
      return null
    }

    // Helper to get the path from root to the selected model
    const getPathToModel = (nodes: any[], modelId: string, path: any[] = []): any[] | null => {
      for (const node of nodes) {
        // Try this path
        const newPath = [...path, node]

        // If this is the target, return the path
        if (node.model.id === modelId) return newPath

        // If this node has children, search them
        if (node.children && node.children.length > 0) {
          const foundPath = getPathToModel(node.children, modelId, newPath)
          if (foundPath) return foundPath
        }
      }

      return null
    }

    // Find the selected model node
    const selectedNode = findNodeById(modelTree.model_tree, modelId)
    if (!selectedNode) return null

    // Get the path from root to the selected model
    const pathToSelected = getPathToModel(modelTree.model_tree, modelId)
    if (!pathToSelected) return null

    // Create a completely new filtered tree with only direct ancestors and descendants
    // First, build the path from root to selected model (without any siblings)
    let filteredTree = null
    let currentNode: any = null

    // Work from the top down, creating a clean tree with just the direct path
    for (let i = 0; i < pathToSelected.length; i++) {
      const originalNode = pathToSelected[i]

      // Create a new node with only essential properties
      const newNode: any = {
        model: originalNode.model,
        parent_id: originalNode.parent_id,
        children: [],
      }

      // If this is the first node (root)
      if (i === 0) {
        filteredTree = newNode
        currentNode = newNode
      } else if (currentNode) {
        // Add as a child to the previous node
        currentNode.children = [newNode]
        currentNode = newNode
      }

      // If this is the selected model, preserve all its descendants
      if (originalNode.model.id === modelId && originalNode.children) {
        // Deep clone descendants function
        const cloneDescendants = (node: any): any => {
          if (!node) return null

          const clonedNode = {
            model: node.model,
            parent_id: node.parent_id,
            children: [],
          }

          if (node.children && node.children.length > 0) {
            clonedNode.children = node.children.map((child: any) => cloneDescendants(child))
          }

          return clonedNode
        }

        // Add all descendants of the selected model
        newNode.children = originalNode.children.map((child: any) => cloneDescendants(child))
      }
    }

    return filteredTree
  }, [modelTree, modelId])

  // Determine which tree to show
  // If we have a specific modelId and filteredTreeData, show the filtered tree
  // Otherwise, show the general tree from parentModels
  const treeToShow = filteredTreeData || (parentModels.length > 0 ? parentModels[0] : null)
  const isFilteredView = !!filteredTreeData

  // Render versions content
  const renderVersionsContent = () => {
    if (!modelId) {
      return (
        <Card
          className="h-100"
          style={{
            background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
            border: '1px solid rgba(102, 126, 234, 0.2)',
          }}
        >
          <Card.Header
            style={{
              background: 'transparent',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Card.Title className="text-white mb-0">Model Tree Visualization</Card.Title>
          </Card.Header>
          <Card.Body className="d-flex align-items-center justify-content-center">
            <div className="text-center">
              <GitBranch size={64} className="text-muted mb-3" />
              <h5 className="text-white mb-3">No Model Selected</h5>
              <p className="text-light mb-4" style={{ color: '#e9ecef' }}>
                Please select a model to view its version tree.
              </p>
              <Button variant="primary" onClick={() => navigate(`/models/${projectId}/overview`)}>
                Go to Models Overview
              </Button>
            </div>
          </Card.Body>
        </Card>
      )
    }

    if (isTreeLoading) {
      return (
        <Card
          className="h-100"
          style={{
            background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
            border: '1px solid rgba(102, 126, 234, 0.2)',
          }}
        >
          <Card.Header
            style={{
              background: 'transparent',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Card.Title className="text-white mb-0">Model Tree Visualization</Card.Title>
          </Card.Header>
          <Card.Body className="d-flex align-items-center justify-content-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Card.Body>
        </Card>
      )
    }

    if (!model) {
      return (
        <Card
          className="h-100"
          style={{
            background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
            border: '1px solid rgba(102, 126, 234, 0.2)',
          }}
        >
          <Card.Header
            style={{
              background: 'transparent',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Card.Title className="text-white mb-0">Model Tree Visualization</Card.Title>
          </Card.Header>
          <Card.Body className="d-flex align-items-center justify-content-center">
            <div className="text-center">
              <GitBranch size={64} className="text-muted mb-3" />
              <h5 className="text-white mb-3">Model Not Found</h5>
              <p className="text-light mb-4" style={{ color: '#e9ecef' }}>
                The selected model could not be found.
              </p>
              <Button variant="primary" onClick={() => navigate(`/models/${projectId}/overview`)}>
                Go to Models Overview
              </Button>
            </div>
          </Card.Body>
        </Card>
      )
    }

    if (!parentModels.length) {
      return (
        <Card
          className="h-100"
          style={{
            background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
            border: '1px solid rgba(102, 126, 234, 0.2)',
          }}
        >
          <Card.Header
            style={{
              background: 'transparent',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Card.Title className="text-white mb-0">Model Tree Visualization</Card.Title>
          </Card.Header>
          <Card.Body className="d-flex align-items-center justify-content-center">
            <div className="text-center">
              <GitBranch size={64} className="text-muted mb-3" />
              <h5 className="text-white mb-3">No Model Trees Available</h5>
              <p className="text-light" style={{ color: '#e9ecef' }}>
                There are currently no model trees in this project. Model trees will appear here
                once you have created models.
              </p>
            </div>
          </Card.Body>
        </Card>
      )
    }

    return (
      <Card
        className="h-100"
        style={{
          background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
          border: '1px solid rgba(102, 126, 234, 0.2)',
        }}
      >
        <Card.Header
          style={{ background: 'transparent', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          <Card.Title className="text-white mb-0">Model Tree Visualization</Card.Title>
        </Card.Header>
        <Card.Body className="p-0" style={{ height: 'min(600px, calc(100vh - 300px))' }}>
          <div style={{ height: '100%', overflow: 'auto' }}>
            <ModelTreeVisualization
              key={`${modelId}-${projectId}`}
              parent={treeToShow}
              project={project}
              highlightModelId={modelId}
              showBackButton={!isFilteredView}
            />
          </div>
        </Card.Body>
      </Card>
    )
  }

  return renderVersionsContent()
}

export default ModelVersionsView
